import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Form, Input, Modal, Row, Space, message } from "antd";
import "./AssessmentTableStyles.scss";
import { CompetenciesIcon } from "assets/icons/DetailedProfile";
import NoData from "components/UI/NoData/NoData";
import TextArea from "antd/lib/input/TextArea";
import classNames from "classnames";

const colors = {
  experiences: "#990000CC",
  traits: "#2E4A79E5",
};

interface ExperienceItem {
  title: string;
  description: string;
}

interface PropsType {
  open: boolean;
  setOpen: any;
  view: string;
  setView: any;
  assessment: any;
  saveAssessment: (view: string, data: ExperienceItem[]) => void;
}

const countLines = (
  text: string,
  width: number,
  fontSize: number = 14,
  lineHeight: number = 17
): number => {
  if (!text) return 0;

  // Create a hidden div to calculate text wrapping
  const div = document.createElement("div");
  div.style.position = "absolute";
  div.style.visibility = "hidden";
  div.style.width = `${width}px`;
  div.style.fontSize = `${fontSize}px`;
  div.style.lineHeight = `${lineHeight}px`;
  div.style.whiteSpace = "pre-wrap";
  div.style.wordBreak = "break-word";
  div.style.padding = "8px";
  div.style.boxSizing = "border-box";
  div.textContent = text;

  document.body.appendChild(div);
  const height = div.offsetHeight;
  document.body.removeChild(div);

  // Add some buffer to account for potential rounding issues
  return Math.ceil(height / lineHeight);
};

const AssessmentRow = ({
  rowData,
  index,
  view,
  isLastRow,
  onAdd,
  onDelete,
  onChange,
  canAddNew,
  totalRows,
}: {
  rowData: ExperienceItem;
  index: number;
  view: string;
  isLastRow: boolean;
  onAdd: () => void;
  onDelete: (index: number) => void;
  onChange: (
    index: number,
    field: "title" | "description",
    value: string
  ) => void;
  canAddNew: boolean;
  totalRows: number;
}) => {
  const { title, description } = rowData;
  const isDeleteEnabled = title.trim() !== "" || description.trim() !== "";
  const showDeleteButton = !isLastRow;

  // Refs for the textareas
  const titleRef = useRef<any>(null);
  const descriptionRef = useRef<any>(null);

  // Process text to fit within 4 lines
  useEffect(() => {
    const processText = (text: string): string => {
      // Limit to 4 lines
      const lines = text?.split("\n");
      if (lines?.length > 4) {
        return lines?.slice(0, 4)?.join("\n");
      }
      return text;
    };

    // Apply processing when component mounts or when title/description changes
    if (title !== processText(title)) {
      onChange(index, "title", processText(title));
    }

    if (description !== processText(description)) {
      onChange(index, "description", processText(description));
    }
  }, [title, description]);

  return (
    <div
      className="assessment-row"
      style={{
        display: "flex",
        gap: "10px",
        marginBottom: "10px",
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      <div
        className="title-input"
        style={{
          width: "192px",
          height: "75px",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: colors[view],
        }}
      >
        <textarea
          ref={titleRef}
          placeholder={`Enter title `}
          // maxLength={45}
          value={title}
          className="textarea-styles title-center"
          onChange={(e) => {
            const value = e.target.value;
            const currentLines = title?.split("\n").length;
            const newLines = value?.split("\n").length;

            // Always allow deletions
            if (value?.length < title?.length) {
              onChange(index, "title", value);
              return;
            }

            // First quick check: If new line breaks exceed limit, block immediately
            if (newLines > 4) {
              // message.warning('Maximum 4 lines allowed');
              return;
            }

            // If we have access to the DOM element
            if (titleRef.current?.resizableTextArea?.textArea) {
              const el = titleRef.current.resizableTextArea.textArea;
              const width = el.offsetWidth;

              // Calculate exact line count including wrapping
              const calculatedLines = countLines(value, width);

              if (calculatedLines <= 5) {
                onChange(index, "title", value);
              } else {
                // message.warning('Text exceeds 4 lines limit');
              }
            } else {
              // Fallback if DOM element not available
              onChange(index, "title", value);
            }
          }}
          style={{
            backgroundColor: "transparent",
            color: "white",
            fontSize: "14px",
            lineHeight: "15px",
            textAlign: "center",
            width: "100%",
            height: "100%",
            border: "none",
            resize: "none",
            outline: "none",
            padding: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        />
      </div>
      <div
        className="definition-input"
        style={{
          width: "630px",
          height: "75px",
          borderRadius: "8px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          background: colors[view],
        }}
      >
        <textarea
          ref={descriptionRef}
          placeholder="Enter definition"
          value={description}
          className="textarea-styles"
          onChange={(e) => {
            const value = e.target.value;
            const currentLines = description?.split("\n").length;
            const newLines = value?.split("\n").length;

            // Always allow deletions
            if (value?.length < description?.length) {
              onChange(index, "description", value);
              return;
            }

            // First quick check: If new line breaks exceed limit, block immediately
            if (newLines > 4) {
              // message.warning('Maximum 4 lines allowed');
              return;
            }
            console.log(descriptionRef.current?.resizableTextArea?.textArea);
            // If we have access to the DOM element
            if (descriptionRef.current?.resizableTextArea?.textArea) {
              const el = descriptionRef.current.resizableTextArea.textArea;
              const width = el.offsetWidth;

              // Calculate exact line count including wrapping
              const calculatedLines = countLines(value, width);
              console.log("calculatedLines-------", calculatedLines);
              if (calculatedLines <= 5) {
                onChange(index, "description", value);
              } else {
                // message.warning('Text exceeds 4 lines limit');
              }
            } else {
              // Fallback if DOM element not available
              onChange(index, "description", value);
            }
          }}
          style={{
            backgroundColor: "transparent",
            color: "white",
            fontSize: "14px",
            lineHeight: "15px",
            textAlign: "left",
            width: "100%",
            height: "100%",
            border: "none",
            resize: "none",
            outline: "none",
            padding: "8px",
            overflow: "hidden",
          }}
        />
      </div>
      <div
        className={classNames("action-button", {
          disabled:
            (showDeleteButton && !isDeleteEnabled) ||
            (!showDeleteButton && !canAddNew),
        })}
        style={{
          backgroundColor: colors[view],
          width: "28px",
          height: "75px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "8px",
          fontSize: "20px",
          color: "white",
          cursor: "pointer",
        }}
        onClick={() => {
          if (showDeleteButton) {
            if (isDeleteEnabled) onDelete(index);
          } else {
            if (canAddNew) onAdd();
          }
        }}
      >
        {showDeleteButton ? "−" : "+"}
      </div>
    </div>
  );
};

const AssessmentTableModal: React.FC<PropsType> = ({
  open,
  setOpen,
  view,
  setView,
  assessment,
  saveAssessment,
}) => {
  const [experiencesData, setExperiencesData] = useState<ExperienceItem[]>([]);
  const [traitsData, setTraitsData] = useState<ExperienceItem[]>([]);
  const [canSave, setCanSave] = useState(false);

  const tableData = view === "experiences" ? experiencesData : traitsData;
  const setTableData =
    view === "experiences" ? setExperiencesData : setTraitsData;

  useEffect(() => {
    if (open) {
      const experiences = assessment?.experience || [];
      const traits = assessment?.traits || [];

      const initializeData = (data: ExperienceItem[]) => {
        if (data?.length === 0) {
          // If no data, start with one empty row
          return [{ title: "", description: "" }];
        }
        // If there's existing data, add an empty row at the end
        return [...data, { title: "", description: "" }];
      };

      setExperiencesData(initializeData(experiences));
      setTraitsData(initializeData(traits));
    }
  }, [assessment, open]);

  useEffect(() => {
    // Check if any row has valid content (including the last row)
    const hasValidRow = tableData?.some(
      (row) => row.title.trim() && row.description.trim()
    );
    setCanSave(hasValidRow);
  }, [tableData]);

  const handleCancel = () => {
    setOpen(false);
  };

  const handleAddNewRow = () => {
    // Keep all existing rows and add a new empty row
    setTableData([...tableData, { title: "", description: "" }]);
  };

  const handleDeleteRow = (index: number) => {
    const newTableData = tableData?.filter((_, i) => i !== index);

    // If we deleted the last row or there are no empty rows at the end, add one
    const lastRow = newTableData[newTableData.length - 1];
    if (
      !lastRow ||
      lastRow.title.trim() !== "" ||
      lastRow.description.trim() !== ""
    ) {
      newTableData.push({ title: "", description: "" });
    }

    setTableData(newTableData);
  };

  const handleRowChange = (
    index: number,
    field: "title" | "description",
    value: string
  ) => {
    const newTableData = [...tableData];
    newTableData[index] = { ...newTableData[index], [field]: value };
    setTableData(newTableData);
  };

  const canAddNewRow = (index: number) => {
    const currentRow = tableData[index];
    return (
      currentRow?.title?.trim() !== "" && currentRow?.description?.trim() !== ""
    );
  };

  const handleSave = () => {
    // Filter out empty rows before saving
    const validData = tableData.filter(
      (row) => row.title.trim() && row.description.trim()
    );
    saveAssessment(view === "experiences" ? "experience" : view, validData);
    setOpen(false);
  };

  const handleViewChange = (newView: string) => {
    setView(newView);
  };

  return (
    <Modal
      width={896}
      style={{ marginTop: "10px", marginBottom: "10px" }}
      title={
        <span style={{ fontSize: 18 }}>
          <img
            src={CompetenciesIcon}
            alt="Competencies icon"
            style={{ marginRight: "10px" }}
          />
          {`Assessment tables`}
        </span>
      }
      centered
      open={open}
      onCancel={handleCancel}
      footer={
        <Space
          style={{
            marginLeft: "auto",
            marginTop: "-22px",
            marginBottom: "4px",
          }}
        >
          <Button
            key="back"
            shape="round"
            onClick={handleCancel}
            style={{
              borderColor: "#990000",
              color: "#990000",
              fontSize: "16px",
            }}
          >
            Cancel
          </Button>

          <Button
            key="submit"
            type="primary"
            shape="round"
            onClick={canSave ? handleSave : undefined}
            className={classNames("save-button", { disabled: !canSave })}
            style={{
              backgroundColor: "#BC9623",
              border: "none",
              fontSize: "16px",
              height: "39px",
              cursor: !canSave ? "not-allowed" : "pointer",
              opacity: !canSave ? 0.5 : 1,
            }}
          >
            Save
          </Button>
        </Space>
      }
    >
      <Row className="view-switches">
        <Button
          className={view === "experiences" ? "Experience" : ""}
          onClick={() => handleViewChange("experiences")}
        >
          Experiences
        </Button>
        <Button
          className={view === "traits" ? "traits" : ""}
          onClick={() => handleViewChange("traits")}
        >
          Traits
        </Button>
      </Row>
      {/* <div
        className="assessment-table-headings"
        style={{
          display: "flex",
          gap: "10px",
          marginBottom: "12px",
          marginLeft: "2px",
        }}
      >
        <div
          style={{
            width: "192px",
            fontWeight: 600,
            fontSize: "14px",
            color: "black",
          }}
        >

        </div>
        <div
          style={{
            width: "630px",
            fontWeight: 600,
            fontSize: "14px",
            color: "black",
          }}
        >
          Definitions
        </div>
        <div style={{ width: "28px" }}></div>
      </div> */}
      <div style={{ width: "100%" }}>
        {tableData.map((row, index) => (
          <AssessmentRow
            key={index}
            rowData={row}
            index={index}
            view={view}
            isLastRow={index === tableData.length - 1}
            onAdd={handleAddNewRow}
            onDelete={handleDeleteRow}
            onChange={handleRowChange}
            canAddNew={canAddNewRow(index)}
            totalRows={tableData.length}
          />
        ))}
      </div>
    </Modal>
  );
};

export default AssessmentTableModal;
